<!-- app-subdomain-creators/src/Components/NewsfeedCreatorCommentReplyMenu.vue -->
<template>
  <div ref="menuRoot" class="relative inline-block">
    <!-- Ellipsis button -->
    <button
      ref="btnEl"
      type="button"
      class="flex items-center justify-center w-8 h-8 text-[22px] rounded-full
             hover:bg-zinc-100 text-zinc-500 font-bold leading-none"
      @click.stop="toggleMenu"
      @mouseenter="showTooltip = true"
      @mouseleave="showTooltip = false"
    >
      <span class="sr-only">More</span>
      ⋯
    </button>

    <!-- Small black tooltip above button -->
    <div
      v-if="showTooltip || isOpen"
      class="pointer-events-none absolute -top-8 -left-7 -translate-x-1/2
             bg-black/75 text-white text-xs font-base rounded-lg
             px-3 py-2 shadow-lg whitespace-nowrap z-[9998]"
    >
      {{ tooltip }}
    </div>

    <!-- Teleported dropdown -->
    <teleport to="body">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[9998]"
        @click="closeMenu"
      >
        <div
          ref="dropdownEl"
          class="fixed w-[340px] bg-white rounded-2xl
                 shadow-[0_0_30px_rgba(15,23,42,0.30)]
                 border border-zinc-200/80 z-[9999] px-2"
          :style="dropdownStyle"
          @click.stop
        >
          <!-- little arrow -->
          <div
            class="pointer-events-none absolute overflow-hidden transform -translate-x-1/2"
            :style="arrowStyle"
          >
            <img
              :src="menuArrow"
              alt=""
              class="w-7 rounded-lg"
              :class="menuPlacement === 'top' ? 'rotate-180' : ''"
            />
          </div>

          <div class="py-2">
            <button
              type="button"
              class="w-full text-left px-4 py-2 text-[14px] font-medium text-zinc-900 hover:bg-zinc-100 rounded-md"
              @click="handleClick('edit')"
            >
              Edit
            </button>

            <button
              type="button"
              class="w-full text-left px-4 py-2 text-[14px] font-medium text-zinc-900 hover:bg-zinc-100 rounded-md"
              @click="handleClick('delete')"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- Delete confirmation modal (all logic stays here) -->
    <NewsfeedDeleteCommentReplyConfirmModal
      :is-open="isConfirmOpen"
      :is-submitting="isSubmittingDelete"
      :title="confirmTitle"
      :message="confirmMessage"
      :error-message="deleteErrorMessage"
      @cancel="handleDeleteCancel"
      @confirm="handleDeleteConfirm"
    />
  </div>
</template>

<script setup>
import {
  defineProps,
  defineEmits,
  ref,
  onMounted,
  onBeforeUnmount,
  nextTick,
  computed,
} from 'vue';

import NewsfeedDeleteCommentReplyConfirmModal from '@/Components/NewsfeedDeleteCommentReplyConfirmModal.vue';

const menuArrow = new URL(
  '@/assets/media/images/newsfeed-menu-little-arrow.svg',
  import.meta.url
).href;

const props = defineProps({
  targetId: { type: [Number, String], required: true },
  // 'comment' | 'reply' | 'reply-reply'
  targetType: { type: String, default: 'comment' },
  tooltip: { type: String, default: 'Edit or delete this' },
  apiProxyUrl: {
    type: String,
    default: () => process.env.VUE_APP_API_PROXY_URL || '',
  },
});

const emit = defineEmits(['edit', 'delete']);

const isOpen = ref(false);
const showTooltip = ref(false);
const menuRoot = ref(null);
const btnEl = ref(null);
const dropdownEl = ref(null);

const menuPlacement = ref('bottom'); // 'bottom' | 'top'
const dropdownStyle = ref({ top: '0px', left: '0px' });
const arrowStyle = ref({ top: '-14px', left: '50%' });

// delete confirm state (local to this component)
const isConfirmOpen = ref(false);
const isSubmittingDelete = ref(false);
const deleteErrorMessage = ref('');

// Dynamic modal copy
const confirmTitle = computed(() =>
  props.targetType === 'comment' ? 'Delete Comment?' : 'Delete Reply?'
);

const confirmMessage = computed(() =>
  props.targetType === 'comment'
    ? 'Are you sure you want to delete this comment?'
    : 'Are you sure you want to delete this reply?'
);

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const updatePosition = async () => {
  await nextTick();

  const btn = btnEl.value;
  const dd = dropdownEl.value;
  if (!btn || !dd) return;

  const btnRect = btn.getBoundingClientRect();
  const ddRect = dd.getBoundingClientRect();

  const viewportW = window.innerWidth || document.documentElement.clientWidth;
  const viewportH = window.innerHeight || document.documentElement.clientHeight;

  const spaceBelow = viewportH - btnRect.bottom;
  const spaceAbove = btnRect.top;
  const needed = ddRect.height + 12;

  const showTop = spaceBelow < needed && spaceAbove > spaceBelow;
  menuPlacement.value = showTop ? 'top' : 'bottom';

  // Center the menu on the button
  const menuWidth = ddRect.width || 256;
  let left = btnRect.left + btnRect.width / 2 - menuWidth / 2;
  left = clamp(left, 12, viewportW - menuWidth - 12);

  let top;
  if (menuPlacement.value === 'top') {
    top = btnRect.top - ddRect.height - 12;
  } else {
    top = btnRect.bottom + 12;
  }
  top = clamp(top, 12, viewportH - ddRect.height - 12);

  dropdownStyle.value = {
    left: `${Math.round(left)}px`,
    top: `${Math.round(top)}px`,
  };

  arrowStyle.value =
    menuPlacement.value === 'top'
      ? { bottom: '-17px', left: '50%' }
      : { top: '-18px', left: '50%' };
};

const toggleMenu = async () => {
  if (isOpen.value) {
    isOpen.value = false;
    return;
  }
  isOpen.value = true;
  await updatePosition();
};

const closeMenu = () => {
  isOpen.value = false;
};

const handleClick = (action) => {
  if (action === 'edit') {
    emit('edit', { id: props.targetId, type: props.targetType });
    closeMenu();
  } else if (action === 'delete') {
    deleteErrorMessage.value = '';
    isConfirmOpen.value = true;
    closeMenu();
  }
};

// Confirm modal handlers
const handleDeleteCancel = () => {
  if (isSubmittingDelete.value) return;
  deleteErrorMessage.value = '';
  isConfirmOpen.value = false;
};

const getDeleteErrorMessage = (payload, status) => {
  if (payload && typeof payload === 'object') {
    if (typeof payload.error === 'string' && payload.error.trim()) {
      return payload.error;
    }
    if (typeof payload.message === 'string' && payload.message.trim()) {
      return payload.message;
    }
  }

  if (status === 403) return 'You do not have permission to delete this item.';
  if (status === 404) return 'This item no longer exists.';
  return 'Failed to delete. Please try again.';
};

const handleDeleteConfirm = async () => {
  if (isSubmittingDelete.value) return;
  isSubmittingDelete.value = true;
  deleteErrorMessage.value = '';

  try {
    const encodedId = encodeURIComponent(props.targetId);
    const resp = await fetch(
      `${props.apiProxyUrl}/api/creator-newsfeed/comments/${encodedId}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }
    );

    let payload = null;
    const text = await resp.text().catch(() => '');
    if (text) {
      try {
        payload = JSON.parse(text);
      } catch (err) {
        payload = { message: text };
      }
    }

    if (!resp.ok) {
      deleteErrorMessage.value = getDeleteErrorMessage(payload, resp.status);
      return;
    }

    emit('delete', {
      id: props.targetId,
      type: props.targetType,
      response: payload,
    });
    isConfirmOpen.value = false;
  } catch (err) {
    deleteErrorMessage.value = 'Network error while deleting. Please retry.';
  } finally {
    isSubmittingDelete.value = false;
  }
};

// keep menu aligned while scrolling/resizing
let raf = null;
const onAnyScrollOrResize = () => {
  if (!isOpen.value) return;
  if (raf) cancelAnimationFrame(raf);
  raf = requestAnimationFrame(() => updatePosition());
};

onMounted(() => {
  window.addEventListener('resize', onAnyScrollOrResize, { passive: true });
  window.addEventListener('scroll', onAnyScrollOrResize, true);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onAnyScrollOrResize);
  window.removeEventListener('scroll', onAnyScrollOrResize, true);
  if (raf) cancelAnimationFrame(raf);
});
</script>


